import { CommonApplication, CommonApplicationGroup } from '@codidae/common-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import Button from 'components/button/button';
import Card from 'components/card/card';
import ScreenUsageChart from 'components/screen-usage-chart/screen-usage-chart';
import { useEffect, useMemo, useState } from 'react';
import { IoArrowBack } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import {
  getScreentimeForPeriod,
  getScreentimeLimits,
  lockSpecificApplications,
  unlockSpecificApplications,
} from 'services/screentime';
import { formatTime } from 'utils/date';
import styles from './app-group-page.module.scss';

interface P {
  application?: CommonApplication;
  group?: CommonApplicationGroup;
  type: 'app' | 'group';
  onClose?: () => void;
  childrenId: string;
  period: {
    start: Date;
    end: Date;
  };
  timeRangeName: string;
}

export function AppGroupPage({ application, group, type, childrenId, period, timeRangeName, onClose }: P) {
  const nav = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const { data: screentimes } = useQuery({
    queryKey: ['getScreentimeForPeriod', childrenId, period],
    queryFn: () => getScreentimeForPeriod(Number(childrenId), period.start, period.end),
  });
  const { data: screentimeLimits, refetch: refetchScreentimeLimits } = useQuery({
    queryKey: ['getScreentimeLimits', childrenId],
    queryFn: () => getScreentimeLimits(Number(childrenId)),
  });
  const lockScreentimeMutation = useMutation({
    mutationFn: lockSpecificApplications,
    onSuccess: () => refetchScreentimeLimits(),
  });
  const unlockScreentimeMutation = useMutation({
    mutationFn: unlockSpecificApplications,
    onSuccess: () => refetchScreentimeLimits(),
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const dailyUsages = useMemo(() => {
    if (!screentimes) return [];
    const dus = [] as {
      createdAt: Date;
      usage: number;
    }[];
    screentimes.forEach((screentime) => {
      screentime.screentimes
        .filter(
          (st) =>
            (type === 'app' && st.application.id === application?.id) ||
            (type === 'group' && st.application.groups.some((g) => g.id === group?.id)),
        )
        .forEach((st) => {
          const date = new Date(st.createdAt);
          const existing = dus.find((daily) => daily.createdAt.toDateString() === date.toDateString());
          if (existing) {
            existing.usage += Math.round(st.duration / 60);
          } else {
            dus.push({
              createdAt: date,
              usage: Math.round(st.duration / 60),
            });
          }
        });
    });
    dus.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return dus;
  }, [screentimes, application, type, group]);

  const periodUsage = dailyUsages?.reduce((acc, usage) => acc + usage.usage, 0) || 0;
  const isLocked =
    (type === 'app' &&
      screentimeLimits?.some(
        (limit) => limit.application && limit.application.id === application?.id && limit.isInstantBlock,
      )) ||
    (type === 'group' &&
      screentimeLimits?.some(
        (limit) => limit.application?.groups.some((g) => g.id === group?.id) && limit.isInstantBlock,
      ));
  const groupApps =
    (type === 'group' &&
      screentimes
        ?.flatMap((screentime) => screentime.screentimes)
        .filter((st) => st.application.groups.some((g) => g.id === group?.id))
        .map((st) => st.application)
        .filter((app, index, self) => self.findIndex((a) => a.id === app.id) === index)) ||
    undefined;
  return (
    <div className={`${styles.container} ${isVisible ? styles.visible : ''}`}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleClose} type="button">
          <IoArrowBack size={24} />
        </button>
      </div>
      <div className={styles.content}>
        {application?.icon ? (
          <img className={styles.appIcon} src={`data:image/png;base64,${application.icon}`} alt="app icon" />
        ) : (
          <div className={styles.appIcon} />
        )}
        <h1 className={styles.title}>{type === 'app' ? application?.name : group?.name}</h1>
        {!!dailyUsages && (
          <Card style={{ padding: 15, width: '100%', marginTop: 30 }}>
            <div className={styles.cardTitle}>Temps d&apos;écran {timeRangeName.toLocaleLowerCase()}</div>

            <div className={styles.periodUsage}>{formatTime(periodUsage)}</div>
            <br />
            {!!dailyUsages?.length && (
              <ScreenUsageChart
                dailyUsages={dailyUsages}
                locked={false}
                timeRangeName={timeRangeName}
                timePeriod={period}
                noTitle
              />
            )}
            <br />
            <div className={styles.smallStat}>
              Moyenne quotidienne : {formatTime(Math.round(periodUsage / dailyUsages.length))}
            </div>
          </Card>
        )}
        {type === 'group' && (
          <Card style={{ padding: 15, width: '100%', marginTop: 15 }}>
            <div className={styles.cardTitle}>Applications dans le groupe</div>
            <br />
            <div className={styles.groupApps}>
              {groupApps?.map((app) => (
                <div key={app.id} className={styles.groupApp}>
                  {app.icon ? (
                    <img className={styles.icon} src={`data:image/png;base64,${app.icon}`} alt="app icon" />
                  ) : (
                    <div className={styles.icon} />
                  )}
                  <div className={styles.appName}>
                    <div className={styles.name}>{app.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
        <Card style={{ padding: 15, width: '100%', marginTop: 15, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className={styles.cardTitle}>Actions</div>
          <Button
            onClick={() => {
              nav(
                `/children/parental-control/${childrenId}?tab=apps&applicationId=${application?.id}&groupId=${group?.id}`,
              );
            }}
          >
            AJOUTER UNE LIMITE
          </Button>
          {isLocked ? (
            <Button
              color="green"
              loading={unlockScreentimeMutation.isPending || lockScreentimeMutation.isPending}
              onClick={() => {
                if (type === 'app') {
                  if (!application) return;
                  unlockScreentimeMutation.mutate({
                    childId: Number(childrenId),
                    applicationIds: [application?.id],
                  });
                } else {
                  unlockScreentimeMutation.mutate({
                    childId: Number(childrenId),
                    applicationIds: groupApps?.map((app) => app.id) || [],
                  });
                }
              }}
            >
              DEBLOCAGE
            </Button>
          ) : (
            <Button
              color="red"
              onClick={() => {
                if (type === 'app') {
                  if (!application) return;
                  lockScreentimeMutation.mutate({
                    childId: Number(childrenId),
                    applicationIds: [application.id],
                  });
                } else {
                  lockScreentimeMutation.mutate({
                    childId: Number(childrenId),
                    applicationIds: groupApps?.map((app) => app.id) || [],
                  });
                }
              }}
              loading={unlockScreentimeMutation.isPending || lockScreentimeMutation.isPending}
            >
              BLOCAGE TEMPORAIRE
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}
